import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { usePortfolios } from '@/hooks/usePortfolios';
import type { Portfolio } from '@/hooks/usePortfolios';
import PortfolioDetailModal from './components/PortfolioDetailModal';

function AddPortfolioModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    price: number;
    location: string;
    propertyType: string;
    authorityStatus: string;
  }) => void;
}) {
  const { isDark } = useTheme();
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [location, setLocation] = useState('');
  const [propertyType, setPropertyType] = useState('Daire');
  const [authorityStatus, setAuthorityStatus] = useState('Açık');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!title.trim() || !price.trim() || !location.trim()) {
      setError('Başlık, fiyat ve lokasyon zorunludur.');
      return;
    }
    const p = parseInt(price.replace(/\D/g, ''), 10);
    if (!p || p <= 0) {
      setError('Geçerli bir fiyat girin.');
      return;
    }
    onSubmit({ title: title.trim(), price: p, location: location.trim(), propertyType, authorityStatus });
    setTitle('');
    setPrice('');
    setLocation('');
    setPropertyType('Daire');
    setAuthorityStatus('Açık');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-2xl p-5 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-base font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>Yeni Portföy Ekle</h3>
          <button onClick={onClose} className={`w-8 h-8 flex items-center justify-center rounded-full ${isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'}`}>
            <i className="ri-close-line" />
          </button>
        </div>
        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
        <div className="space-y-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Başlık (örn: Atakum 3+1 Daire)" className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text placeholder:text-dark-muted' : 'bg-light-bg border-light-border text-light-text placeholder:text-light-muted'}`} />
          <input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Fiyat (₺)" type="number" className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text placeholder:text-dark-muted' : 'bg-light-bg border-light-border text-light-text placeholder:text-light-muted'}`} />
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Lokasyon" className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text placeholder:text-dark-muted' : 'bg-light-bg border-light-border text-light-text placeholder:text-light-muted'}`} />
          <div className="grid grid-cols-2 gap-3">
            <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)} className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text' : 'bg-light-bg border-light-border text-light-text'}`}>
              <option value="Daire">Daire</option>
              <option value="Villa">Villa</option>
              <option value="Dükkan">Dükkan</option>
              <option value="Arsa">Arsa</option>
            </select>
            <select value={authorityStatus} onChange={(e) => setAuthorityStatus(e.target.value)} className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text' : 'bg-light-bg border-light-border text-light-text'}`}>
              <option value="Açık">Açık</option>
              <option value="Özel">Özel</option>
            </select>
          </div>
        </div>
        <button onClick={handleSubmit} className="mt-4 w-full py-2.5 bg-secondary-500 hover:bg-secondary-600 text-white text-sm font-medium rounded-lg transition-colors">
          Kaydet
        </button>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  isOpen,
  title,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { isDark } = useTheme();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className={`relative w-full max-w-sm rounded-2xl p-5 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <p className={`text-sm ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          <strong>{title}</strong> silinsin mi?
        </p>
        <p className={`text-xs mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Bu işlem geri alınamaz.</p>
        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className={`flex-1 py-2.5 text-sm font-medium rounded-lg border ${isDark ? 'border-dark-border text-dark-text hover:bg-dark-border' : 'border-light-border text-light-text hover:bg-light-bg'}`}>İptal</button>
          <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors">Sil</button>
        </div>
      </div>
    </div>
  );
}

export default function PortfoyPage() {
  const { isDark } = useTheme();
  const { portfolios, stats, isLoading, addPortfolio, deletePortfolio } = usePortfolios();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('Tümü');
  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Portfolio | null>(null);

  const filteredPortfolios = portfolios.filter((item) => {
    const matchesSearch =
      !search ||
      item.title.toLowerCase().includes(search.toLowerCase()) ||
      item.location.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'Tümü' || item.propertyType === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-secondary-500">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-building-line text-white text-lg" />
            </div>
          </div>
          <div>
            <h1 className={`text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Portföy Yönetimi
            </h1>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              {stats.totalActive} aktif portföy
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="w-full sm:w-auto py-2.5 px-4 bg-secondary-500 hover:bg-secondary-600 text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line" />
          </div>
          Yeni Portföy Ekle
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Aktif Portföy', value: stats.totalActive, icon: 'ri-building-line', color: 'bg-secondary-500' },
          { label: 'Toplam Satış', value: stats.totalSold, icon: 'ri-check-double-line', color: 'bg-green-500' },
          { label: 'Ort. Satış Süresi', value: `${stats.avgSaleDays} gün`, icon: 'ri-time-line', color: 'bg-blue-500' },
          { label: 'Toplam Değer', value: `${(stats.totalValue / 1000000).toFixed(1)}M ₺`, icon: 'ri-money-cny-circle-line', color: 'bg-primary-500' },
        ].map((stat) => (
          <div
            key={stat.label}
            className={`rounded-xl p-3 md:p-4 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}
          >
            <div className={`w-8 h-8 rounded-lg ${stat.color} flex items-center justify-center mb-2`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${stat.icon} text-white text-sm`} />
              </div>
            </div>
            <p className={`text-lg md:text-xl font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              {stat.value}
            </p>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              {stat.label}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className={`rounded-xl p-3 md:p-4 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border flex-1 ${
            isDark ? 'bg-dark-bg border-dark-border' : 'bg-light-bg border-light-border'
          }`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-search-line text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Portföy ara..."
              className={`flex-1 bg-transparent text-sm outline-none ${
                isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
              }`}
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={`px-3 py-2 rounded-lg text-sm border outline-none ${
              isDark ? 'bg-dark-bg border-dark-border text-dark-text' : 'bg-light-bg border-light-border text-light-text'
            }`}
          >
            <option value="Tümü">Tüm Türler</option>
            <option value="Daire">Daire</option>
            <option value="Villa">Villa</option>
            <option value="Dükkan">Dükkan</option>
            <option value="Arsa">Arsa</option>
          </select>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={`rounded-xl overflow-hidden animate-pulse ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
              <div className="h-40 bg-gray-200 dark:bg-gray-700" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                <div className="flex justify-between">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-10" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cards Grid */}
      {!isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPortfolios.map((item) => (
            <div
              key={item.id}
              className={`rounded-xl overflow-hidden transition-all hover:shadow-soft ${
                isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'
              }`}
            >
              {/* Image */}
              <div className="relative h-40 cursor-pointer" onClick={() => setSelectedPortfolio(item)}>
                <img
                  src={item.photo || 'https://readdy.ai/api/search-image?query=Modern%20luxury%20apartment%20interior%20living%20room%20with%20large%20windows%20bright%20natural%20light%20minimalist%20design%20white%20walls%20wooden%20floors%20contemporary%20furniture%20high%20ceiling%20elegant%20decor%20warm%20tones&width=400&height=300&seq=1&orientation=landscape'}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full bg-white/90 text-gray-800`}>
                    {item.propertyType}
                  </span>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    item.authorityStatus === 'Açık'
                      ? 'bg-blue-500/90 text-white'
                      : 'bg-purple-500/90 text-white'
                  }`}>
                    {item.authorityStatus}
                  </span>
                </div>
                {item.viewCount > 50 && (
                  <div className="absolute top-3 right-3">
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-500/90 text-white flex items-center gap-1">
                      <i className="ri-eye-line" />
                      Popüler
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h3
                  className={`text-sm font-semibold line-clamp-2 cursor-pointer ${isDark ? 'text-dark-text' : 'text-light-text'}`}
                  onClick={() => setSelectedPortfolio(item)}
                >
                  {item.title}
                </h3>
                <p className={`text-xs mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                  {item.location}
                </p>

                <div className="flex items-center justify-between mt-3">
                  <p className={`text-lg font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                    {item.price.toLocaleString('tr-TR')} ₺
                  </p>
                  <div className="flex items-center gap-1 text-xs text-primary-500">
                    <i className="ri-eye-line" />
                    <span>{item.viewCount}</span>
                  </div>
                </div>

                <div className={`flex items-center gap-3 mt-3 pt-3 text-xs ${isDark ? 'border-t border-dark-border text-dark-muted' : 'border-t border-light-border text-light-muted'}`}>
                  <span className="flex items-center gap-1">
                    <i className="ri-image-line" />
                    {item.photos} foto
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-file-line" />
                    {item.documents} belge
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" />
                    {item.createdAt}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t dark:border-dark-border border-light-border">
                  <button
                    onClick={() => setSelectedPortfolio(item)}
                    className={`text-xs px-2.5 py-1.5 rounded-lg transition-colors ${isDark ? 'bg-dark-bg hover:bg-dark-border text-dark-text' : 'bg-light-bg hover:bg-light-border text-light-text'}`}
                  >
                    Detay
                  </button>
                  <button
                    onClick={() => setDeleteTarget(item)}
                    className="text-xs px-2.5 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 transition-colors"
                  >
                    Sil
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredPortfolios.length === 0 && (
        <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-dark-border flex items-center justify-center">
            <i className="ri-search-line text-2xl text-gray-400 dark:text-dark-muted" />
          </div>
          <p className={`text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            Portföy bulunamadı
          </p>
        </div>
      )}

      {/* Modals */}
      <AddPortfolioModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onSubmit={(data) => addPortfolio(data)}
      />
      {selectedPortfolio && (
        <PortfolioDetailModal
          portfolio={selectedPortfolio}
          onClose={() => setSelectedPortfolio(null)}
        />
      )}
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        title={deleteTarget?.title || ''}
        onConfirm={() => {
          if (deleteTarget) {
            deletePortfolio(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
